"use client"

import * as React from "react"
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCorners,
  defaultDropAnimationSideEffects,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type UniqueIdentifier,
  type DropAnimation,
} from "@dnd-kit/core"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "@/lib/utils"

// Types
export interface KanbanContextValue<T> {
  columns: Record<string, T[]>
  activeId: UniqueIdentifier | null
  activeItem: T | null
  activeColumn: string | null
  getItemValue: (item: T) => string
}

const KanbanContext = React.createContext<KanbanContextValue<unknown> | null>(null)

function useKanbanContext<T>() {
  const context = React.useContext(KanbanContext) as KanbanContextValue<T> | null
  if (!context) {
    throw new Error("Kanban components must be used within a Kanban provider")
  }
  return context
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.4",
      },
    },
  }),
}

// Root Kanban component
interface KanbanProps<T> {
  value: Record<string, T[]>
  onValueChange: (value: Record<string, T[]>) => void
  getItemValue: (item: T) => string
  children: React.ReactNode
  className?: string
}

export function Kanban<T>({
  value,
  onValueChange,
  getItemValue,
  children,
  className,
}: KanbanProps<T>) {
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null)
  const [activeColumn, setActiveColumn] = React.useState<string | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const findContainer = React.useCallback((id: UniqueIdentifier): string | undefined => {
    if (id in value) return id as string
    for (const [columnId, items] of Object.entries(value)) {
      if (items.some((item) => getItemValue(item) === id)) {
        return columnId
      }
    }
    return undefined
  }, [value, getItemValue])

  const findItem = React.useCallback((id: UniqueIdentifier): T | undefined => {
    for (const items of Object.values(value)) {
      const item = items.find((i) => getItemValue(i) === id)
      if (item) return item
    }
    return undefined
  }, [value, getItemValue])

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    const { active } = event
    setActiveId(active.id)
    setActiveColumn(findContainer(active.id) ?? null)
  }, [findContainer])

  const handleDragOver = React.useCallback((event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeContainer = findContainer(active.id)
    const overContainer = findContainer(over.id) ?? (over.id as string)

    if (!activeContainer || !overContainer) return
    if (activeContainer === overContainer) return

    const activeItems = [...value[activeContainer]]
    const overItems = [...(value[overContainer] || [])]
    const activeIndex = activeItems.findIndex((item) => getItemValue(item) === active.id)
    
    if (activeIndex === -1) return

    const overIndex = overItems.findIndex((item) => getItemValue(item) === over.id)
    const [movedItem] = activeItems.splice(activeIndex, 1)
    const insertIndex = overIndex >= 0 ? overIndex : overItems.length
    overItems.splice(insertIndex, 0, movedItem)

    onValueChange({
      ...value,
      [activeContainer]: activeItems,
      [overContainer]: overItems,
    })
  }, [findContainer, value, getItemValue, onValueChange])

  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setActiveColumn(null)

    if (!over) return

    const activeContainer = findContainer(active.id)
    const overContainer = findContainer(over.id) ?? (over.id as string)

    if (!activeContainer || !overContainer) return

    if (activeContainer === overContainer) {
      const items = [...value[activeContainer]]
      const activeIndex = items.findIndex((item) => getItemValue(item) === active.id)
      const overIndex = items.findIndex((item) => getItemValue(item) === over.id)

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        onValueChange({
          ...value,
          [activeContainer]: arrayMove(items, activeIndex, overIndex),
        })
      }
    }
  }, [findContainer, value, getItemValue, onValueChange])

  const handleDragCancel = React.useCallback(() => {
    setActiveId(null)
    setActiveColumn(null)
  }, [])

  const activeItem = activeId ? findItem(activeId) ?? null : null

  return (
    <KanbanContext.Provider
      value={{
        columns: value,
        activeId,
        activeItem,
        activeColumn,
        getItemValue: getItemValue as (item: unknown) => string,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div 
          className={cn("flex gap-4", className)}
          data-dragging={activeId !== null}
        >
          {children}
        </div>
      </DndContext>
    </KanbanContext.Provider>
  )
}

// Board
interface KanbanBoardProps {
  children: React.ReactNode
  className?: string
}

export function KanbanBoard({ children, className }: KanbanBoardProps) {
  return (
    <div className={cn("flex gap-4 overflow-x-auto pb-4", className)}>
      {children}
    </div>
  )
}

// Column
interface KanbanColumnProps {
  value: string
  children: React.ReactNode
  className?: string
}

export function KanbanColumn({ value, children, className }: KanbanColumnProps) {
  const { setNodeRef } = useSortable({
    id: value,
    data: { type: "column" },
  })

  return (
    <div
      ref={setNodeRef}
      data-column={value}
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-lg bg-muted/30 border border-border",
        className
      )}
    >
      {children}
    </div>
  )
}

// Column header
interface KanbanColumnHeaderProps {
  children: React.ReactNode
  className?: string
}

export function KanbanColumnHeader({ children, className }: KanbanColumnHeaderProps) {
  return (
    <div className={cn("p-3 border-b border-border", className)}>
      {children}
    </div>
  )
}

// Column content
interface KanbanColumnContentProps {
  value: string
  children: React.ReactNode
  className?: string
}

export function KanbanColumnContent({ value, children, className }: KanbanColumnContentProps) {
  const { columns, getItemValue } = useKanbanContext()
  const items = columns[value] || []
  const itemIds = items.map((item) => getItemValue(item as never))

  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <div className={cn("flex flex-col gap-2 p-2 min-h-[120px] flex-1", className)}>
        {children}
      </div>
    </SortableContext>
  )
}

// Item
interface KanbanItemProps {
  value: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onClick?: () => void
}

export function KanbanItem({ value, children, className, disabled, onClick }: KanbanItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: value,
    disabled,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleClick = () => {
    // Only trigger click if not dragging
    if (!isDragging && onClick) {
      onClick()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-dragging={isDragging}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={cn(
        "rounded-md border border-border bg-card p-3 shadow-sm",
        "cursor-grab active:cursor-grabbing",
        "touch-none select-none",
        isDragging && "opacity-50 ring-2 ring-primary z-50",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {children}
    </div>
  )
}

// Overlay
interface KanbanOverlayProps {
  children?: React.ReactNode | ((params: { value: UniqueIdentifier; item: unknown }) => React.ReactNode)
  className?: string
}

export function KanbanOverlay({ children, className }: KanbanOverlayProps) {
  const { activeId, activeItem } = useKanbanContext()

  if (!activeId) return null

  const content = typeof children === "function" 
    ? children({ value: activeId, item: activeItem })
    : children ?? (
        <div className="rounded-md border border-border bg-card p-3 shadow-lg">
          Dragging...
        </div>
      )

  return (
    <DragOverlay dropAnimation={dropAnimationConfig}>
      <div className={cn("rounded-md shadow-xl", className)}>
        {content}
      </div>
    </DragOverlay>
  )
}
